import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    computed,
    effect,
    EnvironmentInjector,
    inject,
    Injector,
    input,
    model,
    output,
    signal,
    untracked,
    viewChild,
    ViewContainerRef,
    WritableSignal
} from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { AppContext } from 'src/app/app/context';
import { Visual } from 'src/app/pbir/visuals/Visual';
import { VisualType } from 'src/app/pbir/visuals/VisualType';
import { UserDatasetSelectedEvent, VisualEditor, VisualEditorComponent } from './VisualEditor.component';
import { MatDialog } from '@angular/material/dialog';
import { DatasetEditorDialogComponent, DatasetEditorDialogData, DatasetEditorDialogResult } from './dialogs/dataset-editor-dialog.component';
import { RoleItemGroupExpansionState, VisualFieldRole } from 'src/app/pbir/visuals/VisualFieldRole';
import { VisualField } from 'src/app/pbir/visuals/VisualField';

@Component({
  selector: 'visual-editor-layout-section',
  templateUrl: './LayoutSection.component.html',
  styleUrls: ['./LayoutSection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.dataset-column-collapsed]': 'datasetColumnCollapsed()',
    '[class.layout-column-collapsed]': 'layoutColumnCollapsed()',
  },
  standalone: false
})
export class VisualEditorLayoutComponent {
  readonly appContext = inject(AppContext);
  readonly appFeatures = this.appContext.features;

  readonly environmentInjector = inject(EnvironmentInjector);
  readonly injector = inject(Injector);

  readonly allowColumnCollapse = computed(() => this.appFeatures()?.pivotStudio?.collapseAllSections);

  readonly busy = input(false);

  readonly dialog = inject(MatDialog);
  readonly visualEditor = inject(VisualEditorComponent);
  
  readonly rolesDroppedField = signal<VisualField>(null);

  readonly datasetSelect = viewChild<MatSelect>("datasetSelect");

  readonly Mode = VisualEditorLayout.Mode;
  readonly modePrefKey = `VisualEditor_layout_mode`;
  readonly mode = model<VisualEditorLayout.Mode>(this.visualEditor.userPrefs.get(this.modePrefKey, VisualEditorLayout.Mode.Fields));

  readonly hasInvalidCustomField = computed(() => !!this.visual()?.fields?.hasInvalidCalculatedField());

  toggleMode() {
    this.mode.update(
      x => (x == VisualEditorLayout.Mode.Fields)
        ? VisualEditorLayout.Mode.Style
        : VisualEditorLayout.Mode.Fields);

    this.visualEditor.userPrefs.set(this.modePrefKey, this.mode());
  }


  readonly productDatasets = this.visualEditor.productDatasets;
  readonly userDatasets = this.visualEditor.userDatasets;

  readonly allDatasets = computed(() => [
    ...this.productDatasets(),
    ...this.userDatasets(),
  ]);

  readonly dataset = input<VisualEditor.Dataset>(null);
  readonly datasetChange = output<VisualEditor.Dataset>();

  onDatasetChange(value: VisualEditor.Dataset, matSelect: MatSelect) {
    matSelect.writeValue(this.dataset());

    if (!value) {
      return;
    }

    this.datasetChange.emit(value);
  }

  // Compute max width of the dataset icons
  // We make all icons use up max width so they look aligned
  readonly datasetIconMaxWidth = computed(() => {
    function maxWidth(datasets: VisualEditor.Dataset[]) {
      return datasets?.reduce((w, d) => w = Math.max(w, d.icon()?.widthRatio ?? 1), 1) ?? 1;
    }

    let w = Math.max(maxWidth(this.productDatasets()), maxWidth(this.userDatasets()));

    return w;
  });

  readonly datasetColumnCollapsedPrefKey = `VisualEditor.datasetColumnCollapsed`;
  readonly datasetColumnCollapsed = signal(this.visualEditor.userPrefs.get(this.datasetColumnCollapsedPrefKey, false));
  setDatasetColumnCollapsed(value: boolean) {
    this.datasetColumnCollapsed.set(value);
    this.visualEditor.userPrefs.set(this.datasetColumnCollapsedPrefKey, value);
  }

  readonly layoutColumnCollapsedPrefKey = `VisualEditor.layoutColumnCollapsed`;
  readonly layoutColumnCollapsed = signal(this.visualEditor.userPrefs.get(this.layoutColumnCollapsedPrefKey, false));
  setLayoutColumnCollapsed(value: boolean) {
    this.layoutColumnCollapsed.set(value);
    this.visualEditor.userPrefs.set(this.layoutColumnCollapsedPrefKey, value);
  }

  readonly visualType = input<VisualType>(null);
  readonly visual = input<Visual>(null);

  readonly fieldRoles = computed(() => {
    let visual = this.visual();
    if (visual) {
      let roles = [...visual.fieldRoles];
      roles.sort((a, b) => a.displayOrder - b.displayOrder);
      return roles;
    }
    else {
      return [];
    }
  });

  readonly hideUnmatchedFields = model(false);


  readonly styleEditor = computed(() => this.visualType()?.styleEditor);

  private readonly styleEditorContainerRef = viewChild('styleEditorContainer', { read: ViewContainerRef });
  private readonly styleEditorComponentRef = signal<ComponentRef<any>>(null);

  private readonly __styleEditor_RecreateComponentOnTypeChange = effect(() => {
    let styleEditor = this.styleEditor();
    let styleEditorContainerRef = this.styleEditorContainerRef();

    untracked(() => {
      this.destroyStyleEditor();

      if (styleEditor && styleEditorContainerRef) {
        let componentRef = styleEditorContainerRef.createComponent(styleEditor, {
          environmentInjector: this.environmentInjector,
          injector: this.injector
        });

        this.styleEditorComponentRef.set(componentRef);
        componentRef.setInput('busy', untracked(() => this.busy()));
        componentRef.setInput('visual', untracked(() => this.visual()));
        componentRef.changeDetectorRef.detectChanges();
      }
    });
  });

  private readonly __styleEditor__UpdateBusy = effect(() => {
    let componentRef = this.styleEditorComponentRef();
    let busy = this.busy();

    untracked(() => {
      if (componentRef) {
        componentRef.setInput('busy', busy);
        componentRef.changeDetectorRef.detectChanges();
      }
    });
  });

  private readonly __styleEditor__UpdateVisualOnChange = effect(() => {
    let componentRef = this.styleEditorComponentRef();
    let visual = this.visual();

    untracked(() => {
      if (componentRef) {
        componentRef.setInput('visual', visual);
        componentRef.changeDetectorRef.detectChanges();
      }
    });
  });

  private destroyStyleEditor() {
    this.styleEditorComponentRef.update(x => {
      x?.destroy();
      return null;
    });

    this.styleEditorContainerRef()?.clear();
  }

  
  setRoleExpansionState(role: VisualFieldRole, state: RoleItemGroupExpansionState) {
    let roles = this.fieldRoles();
    let isExpanding = state == RoleItemGroupExpansionState.Expanded;

    let baseState = isExpanding ? RoleItemGroupExpansionState.Collapsed : RoleItemGroupExpansionState.None

    for (let role of roles)
        role.expansionState.set(baseState);

    if (isExpanding)
      role.expansionState.set(RoleItemGroupExpansionState.Expanded);  
  }

  addExtendedDatasetClick(e: Event) {
    e.preventDefault();
    this.openExtendedDatasetDialog(null);
  }

  editExtendedDatasetClick(d: VisualEditor.Dataset) {
    this.datasetSelect().close();
    this.openExtendedDatasetDialog(d);
  }

  removeExtendedDatasetClick(d: VisualEditor.Dataset) {
    this.datasetSelect().close();
    this.visualEditor.removeUserdataset(d);
  }

  openExtendedDatasetDialog(d: VisualEditor.Dataset) {
    let dialogRef = this.dialog.open(DatasetEditorDialogComponent, {
      width: '1150px',
      height: '882px',
      maxHeight: '95vh',
      panelClass: 'dialog-no-padding',
      data: <DatasetEditorDialogData>{
        appSettings: this.appContext.settings(),
        userDatasetId: d?.userDatasetId(),
        referencedFields: this.visualEditor.getReferencedFields(),
      }
    }).afterClosed().subscribe((result: DatasetEditorDialogResult) => {
      if (!result) {
        // dialog canceled
        return;
      }

      this.visualEditor.userDatasetSelected({
        userDatasetId: result.userDataset.id,
        refresh: result.refresh,
        validation: result.validationState,
        reset: result.reset,
        reload: result.reload
      });
    });
  }


  ngOnDestroy() {
    this.destroyStyleEditor();
  }
}

export namespace VisualEditorLayout {
  export enum Mode {
    Fields,
    Style
  }
}
